import uniq from "lodash.uniq"

import { getInvestmentTierAmount } from "./getInvestedAmount"


const getPayloads = ({
  sourceProjectId,
  targetProjectId,
  sourceInvestments,
  filesToMove,
  filesToOverride,
  transactionsToMove,
  transactionsToOverride,
  investmentsToMove,
  investmentsToOverride,
  getProjectName,
  customAmountsMap,
  getTargetBatchId,
}) => {
  const sourceProjectName = getProjectName(sourceProjectId)
  const targetProjectName = getProjectName(targetProjectId)

  const moveFilesPayload = filesToMove
    .map(({ id, batchId: sourceBatchId }) =>
      ({
        fileId: id,
        mutation: {
          batchId: getTargetBatchId(sourceBatchId),
          projectId: targetProjectId,
        }
      })
    )

  const overrideFilesPayload = filesToOverride
    .map(({ id, batchId: sourceBatchId, createdAt, investorAccountId, targetInvestmentId }) =>
      ({
        fileId: id,
        mutation: {
          batchId: getTargetBatchId(sourceBatchId),
          projectId: targetProjectId,
          investmentId: targetInvestmentId,
          secondaryKey: ['FIL', investorAccountId, targetInvestmentId, createdAt].join('_'),
        }
      })
    )

  const transferInvestorFilesPayload = {
    update: [
      ...moveFilesPayload,
      ...overrideFilesPayload
    ],
    sourceProjectName,
    targetProjectName,
  }

  const moveTransactionsPayload = transactionsToMove
    .map(transaction =>
      ({
        transactionId: transaction.id,
        mutation: {
          projectId: targetProjectId,
        }
      })
    )

  const overrideTransactionsPayload = transactionsToOverride
    .map(({ id, createdAt, investorAccountId, targetInvestmentId }) =>
      ({
        transactionId: id,
        mutation: {
          projectId: targetProjectId,
          secondaryKey: ['TRA', investorAccountId, targetInvestmentId, createdAt].join('_'),
          investmentId: targetInvestmentId
        }
      })
    )

  const transferTransactionsPayload = {
    update: [
      ...moveTransactionsPayload,
      ...overrideTransactionsPayload
    ],
    sourceProjectName,
    targetProjectName,
  }

  const getSourceInvestment = sourceInvestmentId =>
    sourceInvestments.find(({ id }) => id === sourceInvestmentId)

  const getInvestments = investment => {
    const { id, sourceInvestmentId } = investment

    const investmentCustomAmounts = customAmountsMap[id]
    const hasCustomAmounts = !!investmentCustomAmounts

    if (hasCustomAmounts) {
      const {
        investmentId,
        sourceInvestmentId: _sourceInvestmentId,
        ...customInvestments
      } = investmentCustomAmounts

      return Object.entries(customInvestments)
        .map(([ projectTierId, amount ]) => ({ projectTierId, amount }))
    }

    if (!sourceInvestmentId) {
      return investment.investments
    }

    const targetInvestment = investment
    const sourceInvestment = getSourceInvestment(sourceInvestmentId)

    const tierIds = uniq(
      [
        ...targetInvestment.investments,
        ...sourceInvestment.investments,
      ].map(({ projectTierId }) => projectTierId)
    ).sort()

    const mergedInvestments = tierIds
      .map(projectTierId => {
        const targetAmount = getInvestmentTierAmount(projectTierId, targetInvestment) || 0
        const sourceAmount = getInvestmentTierAmount(projectTierId, sourceInvestment) || 0
        const amount = targetAmount + sourceAmount

        return { projectTierId, amount }
      })

    return mergedInvestments
  }

  const getInvestedAmount = investment =>
    getInvestments(investment)
      .map(({ amount }) => amount)
      .reduce((cur, acc) => cur + acc, 0)

  const moveInvestmentsPayload = investmentsToMove
    .map(investment =>
      ({
        investmentId: investment.id,
        mutation: {
          projectId: targetProjectId,
          projectName: getProjectName(targetProjectId),
          projectKey: `${targetProjectId}_${investment.createdAt}`,
          investments: getInvestments(investment),
          totalAmount: getInvestedAmount(investment),
          receivedAmount: getInvestedAmount(investment)
        }
      })
    )

  const overrideInvestmentsPayload = investmentsToOverride
    .map(investment => {
      const { sourceInvestmentId } = investment
      const sourceInvestment = getSourceInvestment(sourceInvestmentId)

      const {
        investor,
        isSigned,
        isPartiallySigned,
        receiverSourceId,
        receiverSourceName,
      } = sourceInvestment

      const {
        hasAccreditation,
        accreditationExpiresAt,
      } = investor

      const payload = {
        investmentId: investment.id,
        mutation: {
          'investor_hasAccreditation': hasAccreditation,
          'investor_accreditationExpiresAt': accreditationExpiresAt,
          isSigned,
          isPartiallySigned,
          investments: getInvestments(investment),
          totalAmount: getInvestedAmount(investment),
          receivedAmount: getInvestedAmount(investment)
        }
      }

      if (receiverSourceId) {
        payload.mutation.receiverSourceId = receiverSourceId
        payload.mutation.receiverSourceName = receiverSourceName
      }

      return payload
    })

  const deleteInvestmentIds = investmentsToOverride
    .map(investment => {
      const { sourceInvestmentId } = investment
      return sourceInvestmentId
    });

  const transferInvestmentsPayload = {
    sourceProjectId,
    sourceProjectName,
    targetProjectId,
    targetProjectName,
    move: moveInvestmentsPayload,
    override: overrideInvestmentsPayload,
    delete: deleteInvestmentIds,
  }

  return {
    transferInvestmentsPayload,
    transferTransactionsPayload,
    transferInvestorFilesPayload,
  }
}

export default getPayloads
